import {
  Children,
  cloneElement,
  isValidElement,
  useEffect,
  useRef,
  useState,
  type ReactElement,
  type ReactNode,
  type SyntheticEvent,
} from 'react';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Tabs from '@mui/material/Tabs';
import type { TabsProps } from '@mui/material/Tabs';
import { LinkBase, useLocation, useSplatPathBase } from 'ra-core';
import { useParams } from 'react-router-dom';
import type { CandidateShowTabLayoutSection } from '@/pages/Candidate/view/candidateShowTabSections';

function devWarn(message: string) {
  if (import.meta.env.DEV) {
    // eslint-disable-next-line no-console -- intentional dev-only layout contract warning
    console.warn(message);
  }
}

/** Same rule as react-admin `getShowLayoutTabFullPath` (URL segment under the show splat). */
function getShowLayoutTabFullPath(tab: ReactElement<{ path?: string }>, index: number): string {
  const p = tab.props.path;
  if (p) return p;
  return index > 0 ? String(index) : '';
}

type TabChild = ReactElement<{ path?: string }>;

function tabPathsInDropdownSection(section: CandidateShowTabLayoutSection): string[] {
  if (section.layout !== 'dropdown') return [];
  return section.tabs.map((t) => t.props.path).filter((p): p is string => typeof p === 'string');
}

function sliceChildrenIntoSectionGroups(
  children: ReactNode,
  sections: CandidateShowTabLayoutSection[]
): ReactElement[][] {
  const flat = Children.toArray(children).filter(isValidElement) as ReactElement[];
  const sizes = sections.map((s) => s.tabs.length);
  const expected = sizes.reduce((a, b) => a + b, 0);
  if (flat.length !== expected) {
    devWarn(
      `[CandidateTabbedShowLayoutTabs] Received ${flat.length} tab children but sections describe ${expected}. ` +
        'Use prepareCandidateShowTabLayout(…) from candidateShowTabSections.tsx so the bar and routes stay aligned.'
    );
  }

  const groups: ReactElement[][] = [];
  let offset = 0;
  for (const size of sizes) {
    groups.push(flat.slice(offset, offset + size));
    offset += size;
  }
  return groups;
}

function sectionStartIndexes(sections: CandidateShowTabLayoutSection[]): number[] {
  const starts: number[] = [];
  let running = 0;
  for (const s of sections) {
    starts.push(running);
    running += s.tabs.length;
  }
  return starts;
}

/** MUI `sx` arrays from our base row styles plus optional `Tabs` props from react-admin. */
function tabsRowSx(allowGrow: boolean, restSx: TabsProps['sx']): NonNullable<TabsProps['sx']> {
  return [
    { minHeight: 48, ...(allowGrow ? { flex: 1 } : {}) },
    ...(Array.isArray(restSx) ? restSx : restSx ? [restSx] : []),
  ];
}

export interface CandidateTabbedShowLayoutTabsProps extends TabsProps {
  sections: CandidateShowTabLayoutSection[];
  syncWithLocation?: boolean;
}

/**
 * Custom tab bar for `TabbedShowLayout`: reads **`sections`** from `candidateShowTabSections.tsx` and
 * **`children`** injected by react-admin (must be the same nodes as `prepareCandidateShowTabLayout().tabElements`).
 */
const CandidateTabbedShowLayoutTabs = ({
  sections,
  children,
  onChange,
  syncWithLocation,
  value: _syncValue,
  ...tabsRest
}: CandidateTabbedShowLayoutTabsProps) => {
  const params = useParams();
  const location = useLocation();
  const splatPathBase = useSplatPathBase();
  const splat = (params['*'] as string | undefined) ?? '';
  const [openMenu, setOpenMenu] = useState<{ anchor: HTMLElement; sectionIndex: number } | null>(null);
  const menuCloseTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const clearMenuCloseTimer = () => {
    if (menuCloseTimerRef.current != null) {
      clearTimeout(menuCloseTimerRef.current);
      menuCloseTimerRef.current = null;
    }
  };

  const scheduleMenuClose = () => {
    clearMenuCloseTimer();
    menuCloseTimerRef.current = setTimeout(() => {
      setOpenMenu(null);
      menuCloseTimerRef.current = null;
    }, 200);
  };

  useEffect(() => () => clearMenuCloseTimer(), []);

  const sectionGroups = sliceChildrenIntoSectionGroups(children, sections);
  const startIndexes = sectionStartIndexes(sections);

  const tabsSectionIndicesWithTabs = sections
    .map((s, i) => (s.layout === 'tabs' && (sectionGroups[i]?.length ?? 0) > 0 ? i : null))
    .filter((i): i is number => i != null);

  const lastTabsSectionWithTabs =
    tabsSectionIndicesWithTabs.length > 0
      ? tabsSectionIndicesWithTabs[tabsSectionIndicesWithTabs.length - 1]
      : undefined;

  /** `flex: 1` only on the last tab row when a second tab row exists (otherwise the first row grows and shoves the dropdown to the far right). */
  const shouldTabsRowGrow = (sectionIndex: number) =>
    tabsSectionIndicesWithTabs.length >= 2 && sectionIndex === lastTabsSectionWithTabs;

  const pathSetForGroup = (group: ReactElement[], baseIndex: number) =>
    new Set(group.map((tab, j) => getShowLayoutTabFullPath(tab, baseIndex + j)));

  const valueForGroup = (group: ReactElement[], baseIndex: number) => {
    const allowed = pathSetForGroup(group, baseIndex);
    return allowed.has(splat) ? splat : false;
  };

  const cloneHeader = (tab: TabChild, index: number) =>
    cloneElement(tab, {
      context: 'header' as const,
      value: syncWithLocation ? getShowLayoutTabFullPath(tab, index) : index,
      syncWithLocation,
    } as never);

  const handleTabsChange = (event: SyntheticEvent, next: string | number | false) => {
    onChange?.(event, next);
  };

  const dropdownAnchorRefs = useRef<Record<number, HTMLButtonElement | null>>({});

  return (
    <Box sx={{ width: '100%' }}>
      <Box
        sx={{
          alignItems: 'flex-end',
          borderBottom: 1,
          borderColor: 'divider',
          display: 'flex',
          flexWrap: 'nowrap',
          overflowX: 'visible',
        }}
      >
        {sections.map((section, i) => {
          const groupTabs = sectionGroups[i] ?? [];
          const baseIndex = startIndexes[i] ?? 0;

          if (section.layout === 'tabs') {
            if (groupTabs.length === 0) return null;
            return (
              <Tabs
                key={`candidate-tab-section-${i}`}
                {...tabsRest}
                onChange={handleTabsChange}
                scrollButtons="auto"
                sx={tabsRowSx(shouldTabsRowGrow(i), tabsRest.sx)}
                value={valueForGroup(groupTabs, baseIndex)}
                variant="scrollable"
              >
                {groupTabs.map((tab, j) => cloneHeader(tab, baseIndex + j))}
              </Tabs>
            );
          }

          if (groupTabs.length === 0) return null;

          const pathsInThisDropdown = tabPathsInDropdownSection(section);
          const dropdownMatchesRoute = pathsInThisDropdown.includes(splat);

          return (
            <Box
              key={`candidate-tab-section-${i}`}
              onMouseEnter={() => {
                clearMenuCloseTimer();
                const anchor = dropdownAnchorRefs.current[i];
                if (anchor) setOpenMenu({ anchor, sectionIndex: i });
              }}
              onMouseLeave={scheduleMenuClose}
              sx={{
                alignItems: 'flex-end',
                display: 'inline-flex',
                flexShrink: 0,
                position: 'relative',
              }}
            >
              <Button
                color="inherit"
                endIcon={<ExpandMoreIcon fontSize="small" sx={{ ml: -0.5 }} />}
                onClick={(e) => {
                  clearMenuCloseTimer();
                  setOpenMenu({ anchor: e.currentTarget, sectionIndex: i });
                }}
                ref={(el) => {
                  dropdownAnchorRefs.current[i] = el;
                }}
                sx={(theme) => ({
                  borderRadius: 0,
                  flexShrink: 0,
                  minHeight: 48,
                  px: 2,
                  textTransform: 'uppercase',
                  ...theme.typography.button,
                  ...(dropdownMatchesRoute
                    ? {
                        borderBottom: 2,
                        borderColor: 'primary.main',
                        color: 'primary.main',
                      }
                    : {}),
                })}
              >
                {section.menuLabel}
              </Button>
              {openMenu?.sectionIndex === i ? (
                <Menu
                  anchorEl={openMenu.anchor}
                  disablePortal
                  onClose={() => {
                    clearMenuCloseTimer();
                    setOpenMenu(null);
                  }}
                  open
                  slotProps={{
                    root: {
                      sx: { zIndex: (theme) => theme.zIndex.modal },
                    },
                  }}
                >
                  {groupTabs.map((tab) => {
                    const path = String(tab.props.path);
                    const pathname = `${splatPathBase}/${path}`;
                    const label = section.layout === 'dropdown' ? (section.menuItemLabels?.[path] ?? path) : path;
                    return (
                      <MenuItem
                        key={path}
                        component={LinkBase}
                        onClick={() => {
                          clearMenuCloseTimer();
                          setOpenMenu(null);
                        }}
                        selected={splat === path}
                        to={{ ...location, pathname }}
                      >
                        {label}
                      </MenuItem>
                    );
                  })}
                </Menu>
              ) : null}
            </Box>
          );
        })}
      </Box>
    </Box>
  );
};

export default CandidateTabbedShowLayoutTabs;
